import React, { useEffect, useState } from 'react';
import { Worlds, errorMessage } from '../../api/client';
import { World, WorldSettings } from '../../types';
import { Loading, Note, Section, Toggle } from '../ui/primitives';
import { fmtDateTime, genLabel, statusInfo } from '../../utils/format';
import { getRegionFriendlyName } from '../../utils/regionUtils';
import RegionDialog from './RegionDialog';

interface Props {
  world: World;
  refreshKey: number;
  /** Something about the world was edited here; the parent should refetch the worlds list. */
  onWorldChanged?: () => void;
}

const RULES: { key: keyof Omit<WorldSettings, 'worldId'>; label: string; help: string }[] = [
  { key: 'gravityEnabled', label: 'Gravity', help: 'Sand, gravel and entities fall.' },
  { key: 'redstoneEnabled', label: 'Redstone', help: 'Redstone components update and tick.' },
  { key: 'fluidFlowEnabled', label: 'Fluid flow', help: 'Water and lava spread.' },
  { key: 'allowVisitors', label: 'Allow visitors', help: 'Other players can visit the world.' },
];

/** World rules (read-only - changed in-game) plus world facts and version. */
const SettingsTab: React.FC<Props> = ({ world, refreshKey, onWorldChanged }) => {
  const [settings, setSettings] = useState<WorldSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Region: shown from local state so the row updates immediately after a change, before the
  // worlds list has been refetched.
  const [region, setRegion] = useState(world.region);
  const [regionOpen, setRegionOpen] = useState(false);
  const [regionNotice, setRegionNotice] = useState<string | null>(null);
  useEffect(() => setRegion(world.region), [world.region]);

  const st = statusInfo(world.status);
  const canChangeRegion = !st.active && !st.busy;
  const onRegionChanged = (code: string) => {
    setRegion(code);
    setRegionNotice(`Region changed to ${getRegionFriendlyName(code)}. It takes effect the next time the world starts.`);
    onWorldChanged && onWorldChanged();
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(settings == null);
    Worlds.settings(world.id)
      .then((s) => {
        if (!cancelled) {
          setSettings(s);
          setError(null);
        }
      })
      .catch((e) => !cancelled && setError(errorMessage(e, 'Failed to load world settings.')))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [world.id, refreshKey]);

  const generation = (world.generationType || 'VANILLA').toUpperCase();

  return (
    <>
      <Section title="World rules" blurb="What ticks, spreads and who can drop by while the world runs.">
        {error && <Note tone="danger">{error}</Note>}
        {loading && !settings ? (
          <Loading />
        ) : settings ? (
          <>
            <Note>
              Rules are changed in-game from the <span className="kbd">/world settings</span> menu while in your Bakery World.
              This page shows the current values.
            </Note>
            <div className="rules">
              {RULES.map((r) => (
                <div className="rule" key={r.key}>
                  <span>
                    <b>{r.label}</b>
                    <small>{r.help}</small>
                  </span>
                  <Toggle checked={!!settings[r.key]} disabled label={r.label} />
                </div>
              ))}
            </div>
          </>
        ) : null}
      </Section>

      <Section title="World" blurb="Where it runs and how it was generated.">
        {regionNotice && <Note tone="ok">{regionNotice}</Note>}
        <div className="env">
          <div className="env-row">
            <span>
              <b>Region</b>
              <small>{canChangeRegion ? 'Where the world is hosted. Closer to you means lower ping.' : 'Stop the world to change its region.'}</small>
            </span>
            <span className="env-val-acts">
              <span className="val">{getRegionFriendlyName(region)}</span>
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => setRegionOpen(true)}
                disabled={!canChangeRegion}
                title={canChangeRegion ? 'Change the hosting region' : 'The world must be offline to change its region'}
              >
                Change
              </button>
            </span>
          </div>
          <div className="env-row">
            <span>
              <b>Generation</b>
            </span>
            <span className="val">{genLabel(world)}</span>
          </div>
          {generation === 'VANILLA' && world.seed && (
            <div className="env-row">
              <span>
                <b>Seed</b>
              </span>
              <span className="val">{world.seed}</span>
            </div>
          )}
          <div className="env-row">
            <span>
              <b>Created</b>
            </span>
            <span className="val">{fmtDateTime(world.createdAt)}</span>
          </div>
          <div className="env-row">
            <span>
              <b>Last played</b>
            </span>
            <span className="val">{world.lastAccessedAt ? fmtDateTime(world.lastAccessedAt) : 'never'}</span>
          </div>
          <div className="env-row">
            <span>
              <b>World ID</b>
              <small>Handy when reporting a problem.</small>
            </span>
            <span className="val">{world.id}</span>
          </div>
        </div>
      </Section>

      <Section title="Version" blurb="The Minecraft version this world last ran on.">
        <div className="env">
          <div className="env-row">
            <span>
              <b>{world.lastUsedVersion ? `Minecraft ${world.lastUsedVersion}` : 'Not started yet'}</b>
              <small>{world.lastUsedVersion ? 'The world is upgraded automatically when it next starts on a newer supported version.' : 'The version is recorded the first time the world starts.'}</small>
            </span>
            {world.isTesting && <span className="chip">testing world</span>}
          </div>
        </div>
      </Section>

      <RegionDialog open={regionOpen} world={{ ...world, region }} onClose={() => setRegionOpen(false)} onChanged={onRegionChanged} />
    </>
  );
};

export default SettingsTab;
